import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import {
  VolunteerActivism,
  LocalShipping,
  Store,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ContactTiles from '../components/shared/ContactTiles';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigateToFoodParcels = () => {
    navigate('/food-parcels');
  };

  const handleNavigateToVolunteer = () => {
    navigate('/volunteer');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Welcome to New Life Foodbarn
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Supporting our Gold Coast community with affordable fresh food, love, and hope since 2008.
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          New Life Foodbarn welcomes people experiencing financial difficulties, pensioners, health care and senior card holders. 
          We provide cheaper than normal rates for fresh food from local markets and handpicked goods from supermarkets and other companies.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleNavigateToFoodParcels}
            sx={{ px: 4, py: 1.5 }}
          >
            🛒 Order Food Parcels
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleNavigateToVolunteer}
            sx={{ px: 4, py: 1.5 }}
          >
            🤝 Volunteer With Us
          </Button>
        </Box>
      </Box>

      {/* About Us Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          About Us
        </Typography>
        <Card sx={{ p: 4 }}>
          <CardContent>
            <Typography variant="body1" paragraph>
              New Life Community Care Foodbarn was born in 2008 to reach out into the Gold Coast community with love, support and hope. 
              Since the opening, many volunteers have enabled us to reach out and help thousands of people in need.
            </Typography>
            <Typography variant="body1" paragraph>
              We believe that loving support and acceptance is a vital key in helping so many who have suffered loss, hardship, neglect, 
              abuse or health issues that have come their way. In order to further address this we also run a well accepted 12 step 
              recovery program to help make a difference in our Gold Coast community. New Life Community Care Accommodation exists for 
              those who may require assistance with emergency accommodation.
            </Typography>
            <Typography variant="body1" paragraph>
              We hope to, in the future, on our 3-acre complex, have another 30 units for over 50's with affordable rents and 
              opportunities to work in our welfare services, helping others and giving back to our community. We understand that many 
              over 50's experience a hard time in finding employment. By welcoming them into our community organisation we increase 
              their socialisation, boost their confidence and, for us, get some much needed volunteer assistance.
            </Typography>
            <Typography variant="body1" paragraph>
              The amazing work achieved in our community would not be possible without the assistance of some 15-25 committed volunteers each day.
            </Typography>
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Impact:</strong> New Life Community Care is committed to the needs of support for people in hardship and need. 
                The Foodbarn is only one arm of the services the charity runs that support over 17 charities and feed thousands of 
                mouths a month and save somewhere between 25,000 to 30,000 tons a month of waste food going to land fill.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Box>

      {/* Operating Hours Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          FoodBarn Times
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Store sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    The Foodbarn Shop
                  </Typography>
                </Box>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body1" paragraph>
                    <strong>Wednesday:</strong> 10:00 AM – 12:00 PM
                  </Typography>
                  <Typography variant="body1" paragraph>
                    <strong>Thursday:</strong> 9:30 AM – 12:00 PM
                  </Typography>
                  <Typography variant="body1">
                    <strong>Saturday:</strong> 9:00 AM – 11:30 AM
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <LocalShipping sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    Food Parcels
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  The Foodbarn Parcels are open on <strong>Wednesday & Thursday</strong> each week for pickup & delivery.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleNavigateToFoodParcels}
                  sx={{ mt: 2 }}
                >
                  Order Food Parcels
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Volunteer Section */}
      <Box sx={{ mb: 6 }}>
        <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box display="flex" justifyContent="center" mb={2}>
              <VolunteerActivism sx={{ fontSize: 48 }} />
            </Box>
            <Typography variant="h4" component="h2" gutterBottom>
              Volunteers Needed
            </Typography>
            <Typography variant="h6" paragraph>
              The <strong>Foodbarn</strong> needs volunteers!
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              <em>Any age welcome. Do you need to get out and feel great about yourself? 
              Come and help the team making a difference in thousands of lives.</em>
            </Typography>
            <Typography variant="body1" paragraph>
              We are a registered workplace for Community Services, Centrelink & SPER.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleNavigateToVolunteer}
              sx={{ 
                mt: 2, 
                px: 4, 
                py: 1.5,
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Enquire About Volunteering
            </Button>
          </CardContent>
        </Card>
      </Box>

      {/* Contact Information */}
      <ContactTiles title="Contact Us" />
    </Container>
  );
};

export default HomePage; 